import { CSSProperties } from 'react';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import colors from '../../../../assets/theme/base/colors';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { demographicGroupLabel, formatRelativeGap, movementTypeDisplayNames } from '../Shared/deviationsCommon';
import { formatVarianceHeadcount } from './justificationsHelper';
import { formatMovementDate } from './IndividualJustifications/recruitmentPromotionMovementsHelper';

interface PriorityBreakdownModalProps {
    open: boolean;
    onClose: () => void;
    movement: DeviationsDto.RecruitmentPromotionMovementDto | null;
    occupationalLevelDisplayName: string;
}

const tableStyle: CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: colors.white.main,
};

const headCell: CSSProperties = {
    padding: '8px 12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textAlign: 'left',
    color: colors.ui.secondaryText,
    backgroundColor: colors.ui.subtotalRowBackground,
    border: `1px solid ${colors.ui.border}`,
};

const headCellRight: CSSProperties = { ...headCell, textAlign: 'right' };

const baseCell: CSSProperties = {
    padding: '8px 12px',
    fontSize: '0.8125rem',
    color: colors.dark.main,
    border: `1px solid ${colors.ui.border}`,
};

const numericCell: CSSProperties = { ...baseCell, textAlign: 'right', fontWeight: 600 };

function gapTextColour(value: number | null): string {
    if (value === null || value === undefined || value === 0) return colors.ui.mutedText;
    return value < 0 ? colors.deviationBadge.negative.text : colors.deviationBadge.positive.text;
}

export function PriorityBreakdownModal({ open, onClose, movement, occupationalLevelDisplayName }: PriorityBreakdownModalProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <CustomDialogTitle onClose={onClose}>Priority Breakdown</CustomDialogTitle>
            <DialogContent dividers>
                {movement && (
                    <>
                        <MDTypography variant="body2" sx={{ fontWeight: 600 }}>
                            {occupationalLevelDisplayName} - {formatMovementDate(movement.movementDate)}
                        </MDTypography>
                        <MDTypography variant="body2" sx={{ mb: 2, color: colors.ui.secondaryText }}>
                            {movement.fullName} - {movementTypeDisplayNames[movement.movementTypeId] ?? 'Movement'}
                        </MDTypography>

                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={{ ...headCell, width: '64px' }}>Rank</th>
                                    <th style={headCell}>Demographic group</th>
                                    <th style={{ ...headCellRight, width: '120px' }}>Headcount Gap</th>
                                    <th style={{ ...headCellRight, width: '96px' }}>% Gap</th>
                                    <th style={{ ...headCellRight, width: '128px' }}>Relative % Gap</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movement.priorityBreakdown.map(row => {
                                    const rowStyle: CSSProperties = row.isAppointed
                                        ? { backgroundColor: colors.deviationBadge.appointedRow }
                                        : {};
                                    const labelStyle: CSSProperties = row.isAppointed
                                        ? { ...baseCell, fontWeight: 700, color: colors.ui.activeBackground }
                                        : baseCell;
                                    const rankStyle: CSSProperties = {
                                        ...baseCell,
                                        fontWeight: 700,
                                        color: row.isAppointed ? colors.ui.activeBackground : colors.ui.secondaryText,
                                    };
                                    return (
                                        <tr key={`${row.raceId}-${row.genderId}`} style={rowStyle}>
                                            <td style={rankStyle}>{row.priorityRank ?? '—'}</td>
                                            <td style={labelStyle}>
                                                {demographicGroupLabel(row.raceId, row.genderId)}
                                                {row.isAppointed && (
                                                    <MDTypography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                                                        - appointed
                                                    </MDTypography>
                                                )}
                                            </td>
                                            <td style={{ ...numericCell, color: gapTextColour(row.gapHeadcount) }}>
                                                {formatVarianceHeadcount(row.gapHeadcount)}
                                            </td>
                                            <td style={{ ...numericCell, color: gapTextColour(row.gapPercentage) }}>
                                                {formatRelativeGap(row.gapPercentage)}
                                            </td>
                                            <td style={{ ...numericCell, color: gapTextColour(row.relativeGapPercentage) }}>
                                                {formatRelativeGap(row.relativeGapPercentage)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <MDTypography variant="caption" sx={{ display: 'block', mt: 2, color: colors.ui.secondaryText }}>
                            Negative gap = under-represented. Positive gap = over-represented. NP = Not Prioritized - Foreign Nationals have no EE Plan target.
                        </MDTypography>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="contained" color="primary" size="small">Close</MDButton>
            </DialogActions>
        </Dialog>
    );
}
